import isDate from "date-fns/isDate";
import parseISO from "date-fns/parseISO";
import startOfToday from "date-fns/startOfToday";
import isAfter from "date-fns/isAfter";
import isEqual from "date-fns/isEqual";
import differenceInCalendarMonths from "date-fns/differenceInCalendarMonths";
import { getDaysInMonth } from "date-fns";
import format from "date-fns/format";
import parse from "date-fns/parse";

export const formatUTCDate = (date: Date, dateFormat:string) => {
  const utcDate = new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds()
  );
  return format(utcDate, dateFormat);
};

export const getAgeInYears = (dateOfBirth:string) =>{
  const ageInMonths = getAgeInMonths(dateOfBirth, new Date()) ?? 0;
  return Math.floor(ageInMonths/12);
};

export const getAgeInMonths = (from?: string | Date, to?: string | Date): number | undefined => {
  if (!from) {
    return undefined;
  }

  const fromDate = isDate(from)
    ? from as Date
    : parseISO(from as string);
  const toDate = to
    ? isDate(to)
      ? to as Date
      : parseISO(to as string)
    : startOfToday();

  if (isAfter(fromDate, toDate) || isEqual(fromDate, toDate)) {
    return 0;
  }

  const wholeMonths = differenceInCalendarMonths(toDate, fromDate);
  const decimalDays = (fromDate.getDate() - toDate.getDate()) / (getDaysInMonth((toDate)) * -1);
  const months = wholeMonths + decimalDays;

  if (isNaN(months)) {
    return undefined;
  }

  return months;
};

/**
 * @param date String representation of a Date
 * @returns Null if 'date' is null/undefined, or date represented as a string in MM/DD/YYYY format
 */
export const toDateString = (date:string|null|undefined) => {
  if(!date) return null;
  const parsedDate = new Date(date);
  return new Date(
    parsedDate.getUTCFullYear(),
    parsedDate.getUTCMonth(),
    parsedDate.getUTCDate()
  ).toLocaleDateString();
};

/**
 * @param weekdayString String representation of a day of the week
 * @returns Empty string if 'weekdayString' is not a day of the week, or the day of the week resolved for locale
 */
export const translateWeekday = (weekdayString:string) => {
  const weekDays = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const weekdayIndex = weekDays.indexOf(weekdayString.toLowerCase());
  if (weekdayIndex < 0) return "";
  const dummyDate = new Date(1900, 0, weekdayIndex);
  const locale = new Intl.DateTimeFormat().resolvedOptions().locale;
  return dummyDate.toLocaleDateString(locale, { weekday: "long" });
};

export const formatStringDate = (date?: string): string | undefined => {
  if (!date) {
    return undefined;
  }
  return format(parse(date, "yyyy-MM-dd", startOfToday()), "P");
};

export const stripTime = (dateString?: string): string | undefined => {
  if (!dateString) {
    return undefined;
  }
  const split = dateString.split("T");

  if (split.length !== 2) {
    return undefined;
  }
  return split[0];
};

// Helper to create Date object from YYYY-MM-DD string w/o converting, returns undefined if invalid format
export const getDateForDateString = (dateString: string) => {
  // Expecting YYYY-MM-DD
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return undefined;

  const [year, month, day] = dateString.split("-").map(Number);
  // Month is 0-based in JS Date
  return new Date(year, month - 1, day);
};

// Helper to format date as YYYY-MM-DD
export const formatDateYMD = (date?: Date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};
